import { ACMEResponse } from '@b2b/types/core';
import angular, { IHttpService } from 'angular';
import { BehaviorSubject, map } from 'rxjs';
import { ThemesHelperService } from '../themesHelper/types';
import { ActiveApp, STORED_VALUE_AUTHORIZE_SLUG } from './types';

export interface AppsState {
  status: 'idle' | 'loading' | 'error' | 'loaded';
  data: ActiveApp[] | null;
}

/**
 * Loads the tenant's active apps (`GET /v2/b2b/apps/activeapps`) and exposes
 * helpers to check which extendables are active. Used to gate features such
 * as gift card redemption behind the stored value app's extendable slug
 * being present for the tenant.
 */
export class AppsService {
  static $inject = ['$http', 'ThemesHelperService'];

  private stateSubject = new BehaviorSubject<AppsState>({ status: 'idle', data: null });
  state$ = this.stateSubject.asObservable();
  activeApps$ = this.stateSubject.pipe(map((model) => (model.status === 'loaded' ? model.data ?? [] : [])));

  constructor(
    private $http: IHttpService,
    private themesHelperService: ThemesHelperService,
  ) {}

  /** True when an active app extends the given slug, ignoring any `@version` suffix. */
  hasExtendable(slug: string): boolean {
    return (this.stateSubject.value.data ?? []).some((app) =>
      (app.extendables ?? []).some(
        (extendable) => typeof extendable?.slug === 'string' && extendable.slug.split('@')[0] === slug,
      ),
    );
  }

  /** True when the stored value (gift card) app is active for the tenant. */
  hasStoredValueApp(): boolean {
    return this.hasExtendable(STORED_VALUE_AUTHORIZE_SLUG);
  }

  /** Loads active apps once; subsequent calls while loading/loaded are no-ops. */
  ensureLoaded(): void {
    const { status } = this.stateSubject.value;
    if (status === 'loading' || status === 'loaded') {
      return;
    }
    this.loadActiveApps();
  }

  loadActiveApps(): void {
    const tenantId = this.themesHelperService.getCompany('tenantId');

    if (!tenantId) {
      this.stateSubject.next({ status: 'error', data: null });
      return;
    }

    this.stateSubject.next({ status: 'loading', data: this.stateSubject.value.data });

    this.$http
      .get<ACMEResponse<ActiveApp[]>>('/v2/api/b2b/apps/activeapps', {
        headers: {
          'x-tenant-id': String(tenantId),
        },
      })
      .then((response) => {
        this.stateSubject.next({ status: 'loaded', data: response.data.payload ?? [] });
      })
      .catch(() => {
        this.stateSubject.next({ status: 'error', data: null });
      });
  }
}

export const AppsServiceModule = angular
  .module('acme.common.service.apps', [])
  .service('AppsService', AppsService).name;
